import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { elementBox } from '@/render/batch';
import { batchPointer, hoverElement } from '@/render/pick';
import { hero, stations } from '@/state/tour';
import { buildRvtLayer, CONTEXT, RVT_LAYER, rvtLayer } from './layer';
import { useRvtStore } from './rvt';

const station = stations.find((s) => s.kind === 'revit');
// a project wider than this is scaled down to it, a family smaller than MIN up to MIN
const FIT = 45;
const MIN = 6;

interface Placement {
  offset: [number, number, number];
  scale: number;
}

// The file's geometry as its own layer on the station's anchor, beside the hero.
export function RevitScene() {
  const meshes = useRvtStore((s) => s.meshes);
  const elements = useRvtStore((s) => s.elements);
  // scale > offset > layer root, placed once the layer is measured
  const holder = useMemo(() => {
    const scale = new THREE.Group();
    const offset = new THREE.Group();
    scale.add(offset);
    return { scale, offset };
  }, []);

  useEffect(() => {
    if (meshes.length === 0) return;
    const { rig, root } = buildRvtLayer(meshes, elements);
    // measured before it is parented, so the boxes are in the file's own frame
    const placement = place(rig.elements.values());
    if (placement) {
      holder.scale.scale.setScalar(placement.scale);
      holder.offset.position.set(...placement.offset);
      holder.offset.add(root);
    }
    rvtLayer.rig = rig;
    return () => {
      hoverElement(null);
      rvtLayer.rig = null;
      rig.dispose();
    };
  }, [meshes, elements, holder]);

  const handlers = useMemo(() => batchPointer(RVT_LAYER, () => true), []);

  if (!station) return null;
  const [x, y, z] = station.position;
  return (
    <group
      position={[x - hero.position[0], y - hero.position[1], z - hero.position[2]]}
      rotation-y={station.rotationY}
    >
      <primitive object={holder.scale} {...handlers} />
    </group>
  );
}

// Centre the building (not its terrain and rooms) on the anchor.
function place(entries: Iterable<Parameters<typeof elementBox>[0]>): Placement | null {
  const all = new THREE.Box3();
  const core = new THREE.Box3();
  const box = new THREE.Box3();
  for (const el of entries) {
    elementBox(el, box);
    all.union(box);
    if (!CONTEXT.test(el.type)) core.union(box);
  }
  const fit = core.isEmpty() ? all : core;
  if (fit.isEmpty()) return null;
  const size = fit.getSize(new THREE.Vector3());
  const extent = Math.max(size.x, size.y, size.z, 1e-3);
  const scale = extent > FIT ? FIT / extent : extent < MIN ? MIN / extent : 1;
  const c = fit.getCenter(new THREE.Vector3());
  // a project's zero is its ground floor; a family stands on its lowest point
  const ground = fit.min.y < 0 && fit.max.y > 0 ? 0 : fit.min.y;
  return { offset: [-c.x, -ground, -c.z], scale };
}
